<template>
  <div class="vs-roi-section">
    <div class="vs-header">
      <div>
        <h5 class="vs-title">Value Story - Cost Avoided</h5>
        <small class="vs-subtitle text-secondary">
          Choose the view that reads best for your audience.
        </small>
      </div>
      <a-radio-group
        v-if="isConfigured"
        v-model:value="variant"
        button-style="solid"
        size="small"
      >
        <a-radio-button value="a">
          Summary
        </a-radio-button>
        <a-radio-button value="b">
          Breakdown
        </a-radio-button>
        <a-radio-button value="c">
          Trend
        </a-radio-button>
      </a-radio-group>
    </div>

    <!-- Guidance when filters aren't set yet -->
    <div v-if="!filters" class="vs-hint">
      <info-circle-outlined /> Pick an operation, devices, and a date range,
      then click <strong>Apply</strong> to load the value story.
    </div>

    <!-- Unconfigured empty state -->
    <div v-else-if="!isConfigured" class="vs-empty">
      <div class="vs-empty-icon"><dollar-outlined /></div>
      <div class="vs-empty-copy">
        <div class="vs-empty-title">Set up your Value Story</div>
        <div class="vs-empty-desc">
          Assign a failure mode to each step and set plant economics once, we
          will show you the dollars saved for every bad cycle the system
          catches, on this page and the device dashboard.
        </div>
        <a-button type="primary" class="mt-3" @click="handleConfigure">
          Configure Value Story
          <template #icon><arrow-right-outlined /></template>
        </a-button>
      </div>
    </div>

    <!-- Error state -->
    <a-alert
      v-else-if="errorMessage"
      type="error"
      show-icon
      :message="errorMessage"
      class="mb-3"
    />

    <!-- Version A: Executive Summary -->
    <div v-else-if="variant === 'a'" class="vs-body">
      <a-spin :spinning="loading">
        <a-row :gutter="[16, 16]">
          <a-col :xs="24" :md="10">
            <div class="hero-card">
              <div class="hero-label">
                Cost avoided · {{ data?.window?.label || '—' }}
              </div>
              <div class="hero-value">
                {{ formatMoney(data?.cost_avoided_total) }}
              </div>
              <div class="hero-sub">
                <span
                  >{{ data?.bad_cycles_caught || 0 }} bad cycles caught</span
                >
                <span class="dot-sep">·</span>
                <span>
                  {{ formatMoney(data?.avg_per_save) }} avg per save
                </span>
              </div>
            </div>
          </a-col>
          <a-col :xs="24" :md="14">
            <a-row :gutter="[12, 12]">
              <a-col :xs="24" :sm="8">
                <div class="mini-card">
                  <div class="mini-label">Annualized</div>
                  <div class="mini-value">
                    {{ formatMoney(data?.annualized) }}
                  </div>
                  <div class="mini-sub">
                    Scaled to {{ data?.operating_days_per_year || 250 }} days/yr
                  </div>
                </div>
              </a-col>
              <a-col :xs="24" :sm="8">
                <div class="mini-card">
                  <div class="mini-label">Per cycle</div>
                  <div class="mini-value">
                    {{ formatMoney(data?.per_cycle) }}
                  </div>
                  <div class="mini-sub">
                    Across {{ data?.total_cycles || 0 }} cycles
                  </div>
                </div>
              </a-col>
              <a-col :xs="24" :sm="8">
                <div class="mini-card">
                  <div class="mini-label">
                    Coverage over EOL
                    <a-tooltip
                      :title="coverageOverEolTooltip"
                      placement="top"
                    >
                      <info-circle-outlined class="mini-label-info" />
                    </a-tooltip>
                  </div>
                  <div class="mini-value">
                    {{
                      data?.detection_lift_pct == null
                        ? '—'
                        : `${data.detection_lift_pct}%`
                    }}
                  </div>
                  <div class="mini-sub">
                    Above baseline EOL testing
                  </div>
                </div>
              </a-col>
            </a-row>
          </a-col>
          <a-col span="24">
            <div class="chart-card">
              <div class="chart-title">Contribution by failure mode</div>
              <highcharts
                v-if="waterfallOptions"
                type="chart"
                :options="waterfallOptions"
              />
              <div v-else class="chart-empty">No cost avoided in window.</div>
            </div>
          </a-col>
        </a-row>
      </a-spin>
    </div>

    <!-- Version B: Detailed Breakdown -->
    <div v-else-if="variant === 'b'" class="vs-body">
      <a-spin :spinning="loading">
        <a-row :gutter="[12, 12]" class="mb-3">
          <a-col :xs="12" :md="6">
            <div class="kpi-card kpi-card--primary">
              <div class="kpi-label">Cost avoided</div>
              <div class="kpi-value">
                {{ formatMoney(data?.cost_avoided_total) }}
              </div>
              <div class="kpi-sub">{{ data?.window?.label || '—' }}</div>
            </div>
          </a-col>
          <a-col :xs="12" :md="6">
            <div class="kpi-card">
              <div class="kpi-label">Annualized</div>
              <div class="kpi-value">{{ formatMoney(data?.annualized) }}</div>
              <div class="kpi-sub">
                {{ data?.operating_days_per_year || 250 }} days/yr
              </div>
            </div>
          </a-col>
          <a-col :xs="12" :md="6">
            <div class="kpi-card">
              <div class="kpi-label">Avg per save</div>
              <div class="kpi-value">{{ formatMoney(data?.avg_per_save) }}</div>
              <div class="kpi-sub">Weighted across modes</div>
            </div>
          </a-col>
          <a-col :xs="12" :md="6">
            <div class="kpi-card">
              <div class="kpi-label">Steps configured</div>
              <div class="kpi-value">
                {{ data?.steps_configured || 0 }}
                <span class="kpi-total">/ {{ data?.total_steps || 0 }}</span>
              </div>
              <div class="kpi-sub">Failure modes assigned</div>
            </div>
          </a-col>
        </a-row>
        <a-row :gutter="[16, 16]">
          <a-col :xs="24" :md="14">
            <div class="chart-card">
              <div class="chart-title">Top steps by cost impact</div>
              <highcharts
                v-if="rankedStepsOptions"
                type="chart"
                :options="rankedStepsOptions"
              />
              <div v-else class="chart-empty">No step-level activity.</div>
            </div>
          </a-col>
          <a-col :xs="24" :md="10">
            <div class="chart-card">
              <div class="chart-title">Cost by failure mode</div>
              <highcharts
                v-if="donutOptions"
                type="chart"
                :options="donutOptions"
              />
              <div v-else class="chart-empty">No mode-level activity.</div>
            </div>
          </a-col>
        </a-row>
      </a-spin>
    </div>

    <!-- Version C: Trend -->
    <div v-else class="vs-body">
      <a-spin :spinning="loading">
        <a-row :gutter="[16, 16]">
          <a-col :xs="24" :md="6">
            <div class="stack-card">
              <div class="stack-block">
                <div class="stack-label">Cost avoided</div>
                <div class="stack-value">
                  {{ formatMoney(data?.cost_avoided_total) }}
                </div>
                <div class="stack-sub">{{ data?.window?.label || '—' }}</div>
              </div>
              <div class="stack-divider" />
              <div class="stack-block">
                <div class="stack-label">Annualized</div>
                <div class="stack-value stack-value--muted">
                  {{ formatMoney(data?.annualized) }}
                </div>
                <div class="stack-sub">
                  Based on {{ data?.operating_days_per_year || 250 }} operating
                  days
                </div>
              </div>
              <div class="stack-divider" />
              <div class="stack-block">
                <div class="stack-label">Cumulative to date</div>
                <div class="stack-value stack-value--muted">
                  {{ formatMoney(data?.cumulative?.total) }}
                </div>
                <div class="stack-sub">
                  Since
                  {{ data?.cumulative?.since_date || 'first cycle recorded' }}
                </div>
              </div>
            </div>
          </a-col>
          <a-col :xs="24" :md="18">
            <div class="chart-card">
              <div class="chart-title">
                Daily cost avoided
                <span class="chart-tz text-secondary">
                  · with cumulative overlay
                </span>
              </div>
              <highcharts
                v-if="timeSeriesOptions"
                type="chart"
                :options="timeSeriesOptions"
              />
              <div v-else class="chart-empty">No cycles in window.</div>
            </div>
          </a-col>
          <a-col span="24">
            <div class="chart-card">
              <div class="chart-title">Breakdown by failure mode</div>
              <a-table
                :columns="breakdownColumns"
                :data-source="breakdownRows"
                :pagination="false"
                size="small"
                row-key="mode"
                bordered
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.dataIndex === 'mode'">
                    <a-tag
                      :style="{
                        background: record.bg,
                        color: record.fg,
                        border: 'none',
                      }"
                    >
                      {{ record.label }}
                    </a-tag>
                  </template>
                  <template v-if="column.dataIndex === 'saves'">
                    {{ record.saves }}
                  </template>
                  <template v-if="column.dataIndex === 'perSave'">
                    {{ formatMoney(record.avg_per_save) }}
                  </template>
                  <template v-if="column.dataIndex === 'total'">
                    <strong>
                      {{ formatMoney(record.total_cost_avoided) }}
                    </strong>
                  </template>
                  <template v-if="column.dataIndex === 'share'">
                    <div class="share-bar-wrap">
                      <div
                        class="share-bar"
                        :style="{
                          width: (record.share_pct || 0) + '%',
                          background: record.fg,
                        }"
                      />
                      <span class="share-label">
                        {{ (record.share_pct || 0).toFixed(1) }}%
                      </span>
                    </div>
                  </template>
                </template>
              </a-table>
            </div>
          </a-col>
        </a-row>
      </a-spin>
    </div>
  </div>
</template>

<script>
import {
  ArrowRightOutlined,
  DollarOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons-vue';
import TelemetryService from 'src/services/telemetry';
import { FAILURE_MODE_MAP } from 'src/utils/valueStory/config';

// Fallback colors for modes the backend returns that aren't in FAILURE_MODES.
const FALLBACK_MODE_STYLE = { bg: '#F1EFE8', fg: '#444441' };

export default {
  components: { ArrowRightOutlined, DollarOutlined, InfoCircleOutlined },
  props: {
    // Payload emitted by RichFilters: { organization, task, devices,
    // datetime_start, datetime_end, ... }
    filters: { type: Object, default: null },
  },
  data() {
    return {
      variant: 'a',
      loading: false,
      isConfigured: true,
      data: null,
      errorMessage: '',
      // Cache per variant so switching tabs after data is fetched doesn't
      // re-hit the network unnecessarily.
      cache: { a: null, b: null, c: null },
      cacheKey: null,
      breakdownColumns: [
        { title: 'Failure mode', dataIndex: 'mode', width: 200 },
        { title: 'Saves', dataIndex: 'saves', width: 100 },
        { title: 'Per save', dataIndex: 'perSave', width: 140 },
        { title: 'Total avoided', dataIndex: 'total', width: 160 },
        { title: 'Share of total', dataIndex: 'share' },
      ],
    };
  },
  computed: {
    coverageOverEolTooltip() {
      return (
        'How much extra defect coverage the system adds on top of the plant\'s ' +
        'baseline End-of-Line testing. Derived as (100 − EOL detection probability) ' +
        'from the Plant Economics you configured — a headroom estimate, not a ' +
        'measured lift.'
      );
    },
    // Adds bg/fg style to each backend breakdown row so the FE can color it.
    breakdownRows() {
      const list = this.data?.breakdown_by_mode || [];
      return list.map((row) => {
        const style = FAILURE_MODE_MAP[row.mode] || FALLBACK_MODE_STYLE;
        return {
          ...row,
          bg: style.bg,
          fg: style.fg,
        };
      });
    },

    waterfallOptions() {
      const b = this.breakdownRows;
      if (!b.length) return null;
      return {
        chart: {
          type: 'waterfall',
          height: 240,
          backgroundColor: 'transparent',
        },
        title: { text: '' },
        credits: { enabled: false },
        xAxis: { categories: [...b.map((x) => x.label), 'Total'] },
        yAxis: { title: { text: 'USD' } },
        tooltip: {
          pointFormatter() {
            return `<b>$${Math.round(this.y).toLocaleString()}</b>`;
          },
        },
        legend: { enabled: false },
        series: [
          {
            name: 'Cost avoided',
            data: [
              ...b.map((x) => ({ y: x.total_cost_avoided, color: x.fg })),
              { isSum: true, color: '#237804' },
            ],
            dataLabels: {
              enabled: true,
              formatter() {
                return `$${Math.round(this.y).toLocaleString()}`;
              },
              style: { fontWeight: '600', textOutline: 'none' },
            },
            pointPadding: 0,
          },
        ],
      };
    },

    rankedStepsOptions() {
      const list = this.data?.top_steps || [];
      if (!list.length) return null;
      const sorted = [...list].sort((a, b) => a.cost_avoided - b.cost_avoided);
      return {
        chart: { type: 'bar', height: 300, backgroundColor: 'transparent' },
        title: { text: '' },
        credits: { enabled: false },
        xAxis: {
          categories: sorted.map((x) => x.step_name || `Step ${x.step_index}`),
          title: { text: null },
        },
        yAxis: { title: { text: 'USD' }, labels: { format: '${value}' } },
        legend: { enabled: false },
        tooltip: {
          pointFormatter() {
            return `<b>$${Math.round(this.y).toLocaleString()}</b><br/>${
              this.saves
            } saves`;
          },
        },
        series: [
          {
            name: 'Cost avoided',
            data: sorted.map((x) => ({
              y: x.cost_avoided,
              saves: x.saves,
              color: '#1890ff',
            })),
            dataLabels: {
              enabled: true,
              formatter() {
                return `$${Math.round(this.y).toLocaleString()}`;
              },
              style: { fontWeight: '600', textOutline: 'none' },
            },
          },
        ],
      };
    },

    donutOptions() {
      const b = this.breakdownRows;
      if (!b.length) return null;
      return {
        chart: { type: 'pie', height: 300, backgroundColor: 'transparent' },
        title: { text: '' },
        credits: { enabled: false },
        tooltip: {
          pointFormatter() {
            return `<b>$${Math.round(
              this.y
            ).toLocaleString()}</b> (${this.percentage.toFixed(1)}%)`;
          },
        },
        plotOptions: {
          pie: {
            innerSize: '65%',
            dataLabels: {
              enabled: true,
              format: '{point.name}<br/><b>${point.y:,.0f}</b>',
              style: { fontWeight: '500', textOutline: 'none' },
            },
          },
        },
        series: [
          {
            name: 'Cost',
            data: b.map((x) => ({
              name: x.label,
              y: x.total_cost_avoided,
              color: x.fg,
            })),
          },
        ],
      };
    },

    timeSeriesOptions() {
      const rows = this.data?.daily_series || [];
      if (!rows.length) return null;
      return {
        chart: { height: 260, backgroundColor: 'transparent' },
        title: { text: '' },
        credits: { enabled: false },
        xAxis: { categories: rows.map((x) => x.date) },
        yAxis: [
          {
            title: { text: 'Daily $' },
            labels: { format: '${value}' },
          },
          {
            title: { text: 'Cumulative $' },
            labels: { format: '${value}' },
            opposite: true,
          },
        ],
        tooltip: { shared: true },
        legend: { enabled: true, align: 'right' },
        series: [
          {
            name: 'Daily cost avoided',
            type: 'column',
            data: rows.map((x) => x.cost_avoided),
            color: '#52c41a',
            yAxis: 0,
          },
          {
            name: 'Cumulative',
            type: 'spline',
            data: rows.map((x) => x.cumulative),
            color: '#1890ff',
            yAxis: 1,
            marker: { enabled: true },
          },
        ],
      };
    },
  },
  watch: {
    filters: {
      handler() {
        this.fetchForCurrentVariant();
      },
      deep: true,
    },
    variant() {
      this.fetchForCurrentVariant();
    },
  },
  methods: {
    fetcherFor(variant) {
      if (variant === 'b') return TelemetryService.fetchValueStoryBreakdown;
      if (variant === 'c') return TelemetryService.fetchValueStoryTrend;
      return TelemetryService.fetchValueStoryExecutive;
    },

    // Cache key derived from the filter set only. Variant lives as the
    // map key inside `cache`, so switching tabs while filters are unchanged
    // must NOT invalidate previously-cached responses.
    buildFiltersKey() {
      if (!this.filters) return null;
      const f = this.filters;
      const extras = [
        f.cycle_type || '',
        f.exclude_timeout_cycles ? '1' : '0',
        f.start_time ?? '',
        f.end_time ?? '',
      ].join('|');
      return `${f.organization}|${f.task}|${f.devices}|${f.datetime_start}|${f.datetime_end}|${extras}`;
    },

    async fetchForCurrentVariant() {
      if (!this.filters) {
        this.data = null;
        return;
      }
      const variant = this.variant;
      const filtersKey = this.buildFiltersKey();

      // Filters changed since the last fetch — drop all per-variant caches.
      if (this.cacheKey !== filtersKey) {
        this.cache = { a: null, b: null, c: null };
        this.cacheKey = filtersKey;
      }

      // Cache hit for this variant + filter set → no network call.
      if (this.cache[variant]) {
        this.data = this.cache[variant];
        this.isConfigured = true;
        this.errorMessage = '';
        return;
      }

      this.loading = true;
      this.errorMessage = '';
      const fetcher = this.fetcherFor(variant);
      const [error, data] = await fetcher(this.filters);
      this.loading = false;
      if (error) {
        const status = error.response?.status;
        if (status === 404) {
          this.isConfigured = false;
          this.data = null;
          return;
        }
        if (status === 403) {
          this.errorMessage = 'You do not have access to this task.';
        } else {
          this.errorMessage =
            error.response?.data?.detail ||
            'Failed to load value story. Please try again.';
        }
        return;
      }
      this.isConfigured = true;
      this.data = data;
      this.cache[variant] = data;
    },

    handleConfigure() {
      const query = this.filters?.task
        ? { openValueStory: this.filters.task }
        : {};
      const { href } = this.$router.resolve({
        name: 'Define Operation',
        query,
      });
      window.open(href, '_blank');
    },

    formatMoney(v) {
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return '$0';
      if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
      if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
      if (n < 10) return `$${n.toFixed(2)}`;
      return `$${Math.round(n).toLocaleString()}`;
    },
  },
};
</script>

<style scoped>
.vs-roi-section {
  background: #fafbfc;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px 20px;
  margin-top: 8px;
}
.vs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
}
.vs-title {
  margin: 0;
  font-weight: 700;
  color: #1a1a1a;
}
.vs-subtitle {
  font-size: 12px;
}
.vs-body {
  min-height: 240px;
}
.vs-hint {
  background: #f0f7ff;
  border: 1px dashed #91d5ff;
  border-radius: 10px;
  padding: 12px 14px;
  color: #0c53b7;
  font-size: 13px;
}

.vs-empty {
  display: flex;
  align-items: center;
  gap: 24px;
  background: #ffffff;
  border: 1px dashed #d9d9d9;
  border-radius: 12px;
  padding: 24px 28px;
}
.vs-empty-icon {
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: 999px;
  background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
  color: #237804;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  border: 1px solid #b7eb8f;
}
.vs-empty-copy {
  flex: 1;
  min-width: 0;
}
.vs-empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 4px;
}
.vs-empty-desc {
  color: #595959;
  font-size: 13px;
  line-height: 1.5;
  max-width: 620px;
}
@media (max-width: 640px) {
  .vs-empty {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 20px;
  }
}

/* Version A */
.hero-card {
  background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
  border: 1px solid #b7eb8f;
  border-radius: 12px;
  padding: 20px 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.hero-label {
  font-size: 11px;
  color: #237804;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 600;
  margin-bottom: 6px;
}
.hero-value {
  font-size: 44px;
  font-weight: 800;
  color: #135200;
  line-height: 1;
}
.hero-sub {
  margin-top: 10px;
  color: #237804;
  font-size: 13px;
  font-weight: 500;
}
.dot-sep {
  margin: 0 8px;
  opacity: 0.6;
}
.mini-card {
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 14px;
  height: 100%;
}
.mini-label {
  font-size: 11px;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.mini-label-info {
  font-size: 12px;
  color: #bfbfbf;
  cursor: help;
  text-transform: none;
  letter-spacing: normal;
}
.mini-label-info:hover {
  color: #8c8c8c;
}
.mini-value {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1;
}
.mini-sub {
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 6px;
}

/* Version B */
.kpi-card {
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 12px 14px;
}
.kpi-card--primary {
  background: linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%);
  border-color: #91d5ff;
}
.kpi-label {
  font-size: 10px;
  color: #595959;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1;
  margin-top: 3px;
}
.kpi-total {
  font-size: 14px;
  color: #8c8c8c;
  font-weight: 500;
}
.kpi-sub {
  font-size: 11px;
  color: #8c8c8c;
  margin-top: 4px;
}

/* Version C */
.stack-card {
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 14px 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.stack-block {
  display: flex;
  flex-direction: column;
}
.stack-label {
  font-size: 11px;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.stack-value {
  font-size: 26px;
  font-weight: 700;
  color: #237804;
  line-height: 1;
  margin-top: 3px;
}
.stack-value--muted {
  color: #1a1a1a;
  font-size: 20px;
}
.stack-sub {
  font-size: 11px;
  color: #8c8c8c;
  margin-top: 3px;
}
.stack-divider {
  height: 1px;
  background: #f0f0f0;
}
.share-bar-wrap {
  position: relative;
  background: #fafafa;
  border-radius: 999px;
  height: 16px;
  overflow: hidden;
}
.share-bar {
  height: 100%;
  border-radius: 999px;
  opacity: 0.75;
}
.share-label {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  font-weight: 600;
  color: #262626;
}

/* Shared */
.chart-card {
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 12px 14px;
}
.chart-title {
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 8px;
}
.chart-tz {
  font-weight: 400;
}
.chart-empty {
  color: #bfbfbf;
  font-size: 13px;
  padding: 32px 0;
  text-align: center;
}
</style>
